import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import sanitize from 'sanitize-html'

import { Translation } from 'cng-common/features/translations/components/translation'
import { getFormatDate } from 'core/classes/helpers/DateHelper'
import { TRANSLATION_KEYS } from 'partner360/components/QBR/translations'
import {
  selectQbrCurrency,
  selectQbrDocumentGeneratedDate,
  selectQbrDocumentGeneratedDateYearPercent,
  selectQBRKeyOptions,
  selectQbrNetNdp,
} from 'partner360/features/QBR'

import { COLORS, negativeHeaders, positiveHeaders, SPLIT_NON_DIGITS_REGEX } from './configs'
import { mapKeyPointsToHighlightData } from './mappers/mapKeyPointsToHighlightData'
import { IHighlightData } from './models/iHighlightData'

import styles from './QBRMainHighlights.module.scss'

export const QBRMainHighlights = () => {
  const keyOptions = useSelector(selectQBRKeyOptions)
  const netNdp = useSelector(selectQbrNetNdp)
  const currency = useSelector(selectQbrCurrency)

  const mappedKeyOptions = useMemo(() => {
    if (!keyOptions) {
      return []
    }

    return keyOptions.map(mapKeyPointsToHighlightData)
  }, [keyOptions])

  const positiveOptions = mappedKeyOptions.filter((option) => option.type === 'Positive')
  const negativeOptions = mappedKeyOptions.filter((option) => option.type === 'Negative')

  const qbrDocumentGeneratedDateYearPercent = useSelector(selectQbrDocumentGeneratedDateYearPercent)
  const qbrDocumentGeneratedDate = useSelector(selectQbrDocumentGeneratedDate)

  const generatedDate = useMemo(
    () => (qbrDocumentGeneratedDate ? getFormatDate(qbrDocumentGeneratedDate, 'date') || '-' : '-'),
    [qbrDocumentGeneratedDate]
  )

  const generatedDateYearPercent = useMemo(
    () => (qbrDocumentGeneratedDateYearPercent ? `${qbrDocumentGeneratedDateYearPercent}%` : '-'),
    [qbrDocumentGeneratedDateYearPercent]
  )

  const getParsedValue = useCallback((originalValue: string) => {
    const [rawValue] = originalValue.split('%')
    const parsedNumber = parseInt(rawValue)
    return parsedNumber
  }, [])

  const getPercentBackgroundColor = useCallback(
    (item: IHighlightData) => {
      const parsedNumber = getParsedValue(item.value)

      if (parsedNumber >= 0 && parsedNumber < 50) {
        return COLORS.redColor
      } else if (parsedNumber >= 50 && parsedNumber < 100) {
        return COLORS.yellowColor
      } else {
        return COLORS.greenColor
      }
    },
    [getParsedValue]
  )

  const getKIBackgroundColor = useCallback(
    (item: IHighlightData) => {
      const parsedNumber = getParsedValue(item.value)
      const [, limit] = item.caption
        .split(SPLIT_NON_DIGITS_REGEX)
        .filter((it) => it !== '')
        .map((it) => parseInt(it))

      const middle = limit / 2

      if (item.type === 'Negative') {
        if (parsedNumber > 0 && parsedNumber <= middle) {
          return COLORS.yellowColor
        } else if (parsedNumber >= middle && parsedNumber <= limit) {
          return COLORS.redColor
        } else {
          return COLORS.greenColor
        }
      } else {
        if (parsedNumber >= 0 && parsedNumber < middle) {
          return COLORS.redColor
        } else if (parsedNumber >= middle && parsedNumber < limit) {
          return COLORS.yellowColor
        } else {
          return COLORS.greenColor
        }
      }
    },
    [getParsedValue]
  )

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.headerMain}>
          <Translation
            text={TRANSLATION_KEYS.settings.headerMain}
            replaceParams={[
              { key: '{date}', value: generatedDate },
              { key: '{percentage}', value: generatedDateYearPercent },
            ]}
          />
        </div>
        <div className={styles.headerSecond}>
          <Translation
            text={TRANSLATION_KEYS.settings.headerSecond}
            replaceParams={[{ key: '{percentage}', value: '' }]}
          />
        </div>
      </div>

      <div className={styles.rootHighlights}>
        <div className={styles.positiveOptions}>
          {positiveOptions.map((itemData, index) => {
            const qbrHeader = positiveHeaders[index]
            return (
              <div key={index} className={styles.positiveOptionRoot}>
                <div className={styles.optionText}>
                  <div className={styles.qbrHighlightHeaderPositive}>
                    <Translation text={qbrHeader} />
                  </div>
                  <pre className={styles.pre}>
                    <div
                      className={styles.positiveOptionCaption}
                      dangerouslySetInnerHTML={{ __html: sanitize(itemData.caption) }}
                    />
                  </pre>
                  <div
                    className={styles.positiveOptionDescription}
                    dangerouslySetInnerHTML={{ __html: sanitize(itemData.description) }}
                  />
                </div>
                <div className={styles.circleRoot}>
                  <div
                    className={styles.optionCircle}
                    style={{
                      background: itemData.isKI
                        ? getKIBackgroundColor(itemData)
                        : getPercentBackgroundColor(itemData),
                    }}
                    dangerouslySetInnerHTML={{ __html: sanitize(itemData.value) }}
                  />
                </div>
              </div>
            )
          })}
        </div>
        <div className={styles.mainText}>
          <Translation text={TRANSLATION_KEYS.settings.keyOptionText} />
          <div>
            {netNdp} {currency}
          </div>
        </div>
        <div className={styles.negativeOptions}>
          {negativeOptions?.map((itemData, index) => {
            const qbrHeader = negativeHeaders[index]
            return (
              <div key={index} className={styles.negativeOptionRoot}>
                <div className={styles.circleRoot}>
                  <div
                    className={styles.optionCircle}
                    style={{
                      background: itemData.isKI
                        ? getKIBackgroundColor(itemData)
                        : getPercentBackgroundColor(itemData),
                    }}
                  >
                    {itemData.value}
                  </div>
                </div>

                <div className={styles.optionText}>
                  <div className={styles.qbrHighlightHeaderNegative}>
                    <Translation text={qbrHeader} />
                  </div>
                  <pre className={styles.pre}>
                    <div
                      className={styles.negativeOptionCaption}
                      dangerouslySetInnerHTML={{ __html: sanitize(itemData.caption) }}
                    />
                  </pre>
                  <div
                    className={styles.negativeOptionDescription}
                    dangerouslySetInnerHTML={{ __html: sanitize(itemData.description) }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
